// hooks/useCart.ts
import { useState, useEffect, useCallback } from 'react';
import { Product, CartStore } from '../types';

const cartSubscribers = new Set<() => void>();

export const useCart = () => {
    const [cartStore, setCartStore] = useState<CartStore>(() => {
        const saved = localStorage.getItem('cart');
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        const updateCart = () => {
            const saved = localStorage.getItem('cart');
            if (saved) {
                setCartStore(JSON.parse(saved));
            }
        };

        cartSubscribers.add(updateCart);
        return () => {
            cartSubscribers.delete(updateCart);
        };
    }, []);

    const updateCartAndNotify = useCallback((newCartOrUpdater: CartStore | ((prev: CartStore) => CartStore)) => {
        const newCart = typeof newCartOrUpdater === 'function' 
            ? newCartOrUpdater(cartStore)
            : newCartOrUpdater;
            
        localStorage.setItem('cart', JSON.stringify(newCart));
        setCartStore(newCart);
        cartSubscribers.forEach(subscriber => subscriber());
    }, [cartStore]);

    const addToCart = useCallback((product: Product & { itemId: string; whatsappNumber: string }) => {
        updateCartAndNotify(currentStore => {
            const itemCart = currentStore[product.itemId] || { 
                items: [], 
                whatsappNumber: product.whatsappNumber 
            };

            const existingItem = itemCart.items.find(item => item.id === product.id);
            
            if (existingItem) {
                const updatedItems = itemCart.items.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: (item.quantity || 0) + (product.quantity || 1) }
                        : item
                );
                return {
                    ...currentStore,
                    [product.itemId]: {
                        ...itemCart,
                        items: updatedItems
                    }
                };
            }
            
            return {
                ...currentStore,
                [product.itemId]: {
                    ...itemCart,
                    items: [...itemCart.items, { ...product, quantity: product.quantity || 1 }]
                }
            };
        });
    }, [updateCartAndNotify]);

    const updateQuantity = useCallback((itemId: string, productId: string, quantity: number) => {
        if (quantity < 1) {
            removeItem(itemId, productId);
            return;
        }
        
        updateCartAndNotify(current => {
            const itemCart = current[itemId];
            if (!itemCart) return current;

            return {
                ...current,
                [itemId]: {
                    ...itemCart,
                    items: itemCart.items.map(item =>
                        item.id === productId
                            ? { ...item, quantity }
                            : item
                    )
                }
            };
        });
    }, [updateCartAndNotify]);

    const removeItem = useCallback((itemId: string, productId: string) => {
        updateCartAndNotify(current => {
            const itemCart = current[itemId];
            if (!itemCart) return current;

            const updatedItems = itemCart.items.filter(item => item.id !== productId);
            
            if (updatedItems.length === 0) {
                const { [itemId]: _, ...rest } = current;
                return rest;
            }

            return {
                ...current,
                [itemId]: {
                    ...itemCart,
                    items: updatedItems
                }
            };
        });
    }, [updateCartAndNotify]);

    const clearCart = useCallback((itemId?: string) => {
        if (itemId) {
            updateCartAndNotify(current => {
                const { [itemId]: _, ...rest } = current;
                return rest;
            });
        } else {
            updateCartAndNotify({});
        }
    }, [updateCartAndNotify]);

    const getTotalPrice = useCallback((itemId?: string) => {
        if (itemId) {
            const itemCart = cartStore[itemId];
            if (!itemCart) return 0;
            return itemCart.items.reduce((sum, item) => 
                sum + (item.salePrice ?? item.mainPrice) * item.quantity, 0
            );
        }
        
        return Object.values(cartStore).reduce((total, itemCart) => 
            total + itemCart.items.reduce((sum, item) => 
                sum + (item.salePrice ?? item.mainPrice) * item.quantity, 0
            ), 0
        );
    }, [cartStore]);

    const getTotalItems = useCallback((itemId?: string) => {
        if (itemId) {
            const itemCart = cartStore[itemId];
            if (!itemCart) return 0;
            return itemCart.items.reduce((total, item) => total + (item.quantity || 1), 0);
        }
        
        return Object.values(cartStore).reduce((total, itemCart) => 
            total + itemCart.items.reduce((sum, item) => sum + (item.quantity || 1), 0), 0
        );
    }, [cartStore]);

    return {
        cartStore,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        getTotalPrice,
        getTotalItems
    };
};